// NPC control panel for automated gameplay

import { useState } from "react";
import { DEFAULT_NPC_CONFIG } from "../types/npc";
import type { NpcConfig, NpcActionLog, StrategyType } from "../types/npc";

interface NpcControlProps {
  config: NpcConfig;
  onConfigChange: (config: NpcConfig) => void;
  actionLog: NpcActionLog[];
  onClearLog: () => void;
}

export function NpcControl({
  config,
  onConfigChange,
  actionLog,
  onClearLog,
}: NpcControlProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggleEnabled = () => {
    onConfigChange({ ...config, enabled: !config.enabled });
  };

  const handleStrategyChange = (strategy: StrategyType) => {
    onConfigChange({ ...config, strategy });
  };

  const handleDelayChange = (delayMs: number) => {
    onConfigChange({ ...config, delayMs });
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <div className="bg-gray-900 border border-gray-700 rounded-lg shadow-xl w-80">
        {/* Header */}
        <div
          className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-800 rounded-t-lg"
          onClick={() => setIsExpanded(!isExpanded)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setIsExpanded(!isExpanded);
            }
          }}
          role="button"
          tabIndex={0}
        >
          <div className="flex items-center gap-2">
            <div
              className={`w-3 h-3 rounded-full ${config.enabled ? "bg-green-500 animate-pulse" : "bg-gray-500"}`}
            />
            <span className="font-medium text-white">NPC</span>
            {config.enabled && (
              <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded">
                ACTIVE
              </span>
            )}
          </div>
          <button className="text-gray-400 hover:text-white">
            {isExpanded ? "▼" : "▲"}
          </button>
        </div>

        {isExpanded && (
          <div className="border-t border-gray-700">
            {/* Controls */}
            <div className="p-3 space-y-3">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between">
                <label
                  htmlFor="npc-enable-toggle"
                  className="text-sm text-gray-300"
                >
                  Enable NPC
                </label>
                <button
                  id="npc-enable-toggle"
                  onClick={handleToggleEnabled}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    config.enabled ? "bg-green-600" : "bg-gray-600"
                  }`}
                  aria-pressed={config.enabled}
                >
                  <div
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      config.enabled ? "translate-x-7" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>

              {/* Strategy Selection */}
              <div>
                <label
                  htmlFor="npc-strategy-select"
                  className="block text-sm text-gray-300 mb-1"
                >
                  Strategy
                </label>
                <select
                  id="npc-strategy-select"
                  value={config.strategy}
                  onChange={(e) =>
                    handleStrategyChange(e.target.value as StrategyType)
                  }
                  className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1.5 text-sm text-white focus:border-blue-500 focus:outline-none"
                  disabled={config.enabled}
                >
                  <option value="random">Random</option>
                  <option value="sequential">
                    Sequential (First Available)
                  </option>
                </select>
                {config.enabled && (
                  <p className="text-xs text-yellow-500 mt-1">
                    Disable npc to change strategy
                  </p>
                )}
              </div>

              {/* Delay Slider */}
              <div>
                <label className="block text-sm text-gray-300 mb-1">
                  Action Delay: {config.delayMs}ms
                </label>
                <input
                  type="range"
                  min="0"
                  max="3000"
                  step="100"
                  value={config.delayMs}
                  onChange={(e) => handleDelayChange(Number(e.target.value))}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>0ms</span>
                  <span>3000ms</span>
                </div>
              </div>
            </div>

            {/* Action Log */}
            <div className="border-t border-gray-700">
              <div className="flex items-center justify-between p-2 bg-gray-800">
                <span className="text-xs font-medium text-gray-400">
                  Action Log
                </span>
                <button
                  onClick={onClearLog}
                  className="text-xs text-gray-500 hover:text-white"
                >
                  Clear
                </button>
              </div>
              <div className="max-h-40 overflow-y-auto">
                {actionLog.length === 0 ? (
                  <div className="p-3 text-xs text-gray-500 text-center">
                    No actions yet
                  </div>
                ) : (
                  <div className="p-2 space-y-1">
                    {actionLog.slice(0, 10).map((entry, idx) => (
                      <div key={idx} className="text-xs">
                        <div className="flex items-center gap-1">
                          <span className="text-gray-500">
                            {entry.timestamp.toLocaleTimeString()}
                          </span>
                          <span className="font-medium text-blue-400">
                            {entry.action}
                          </span>
                        </div>
                        <div className="text-gray-400 pl-14 truncate">
                          {entry.details}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Reset Button */}
            <div className="p-3 border-t border-gray-700">
              <button
                onClick={() => onConfigChange(DEFAULT_NPC_CONFIG)}
                className="w-full text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 py-1.5 rounded"
              >
                Reset to Defaults
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
